import { useState, useEffect, useMemo, useCallback } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { CheckIcon } from 'lucide-react';
import { useAuthStore, selectUser, selectIsAuthLoading } from '@/stores/authStore';
import { getOnboardingStep, isSyntheticEmail, isValidEmail } from '@corates/shared/email';
import { handleError } from '@/lib/error-utils';
import { getPendingInvitationToken, clearPendingInvitationToken } from '@/lib/pendingInvitation';
import { acceptInvitation } from '@/server/functions/invitations.functions';
import { showToast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  hasPendingPlan,
  clearPendingPlan,
  handlePendingPlanRedirect,
  BILLING_MESSAGES,
} from '@/lib/plan-redirect-utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Steps,
  StepsList,
  StepsItem,
  StepsTrigger,
  StepsIndicator,
  StepsSeparator,
  StepsContent,
  StepsViewport,
  StepsPrevTrigger,
  StepsCompletedContent,
} from '@/components/ui/steps';
import { ErrorMessage } from '@/components/auth/ErrorMessage';
import { PrimaryButton } from '@/components/auth/AuthButtons';
import { CodeInput, ResendCode } from '@/components/auth/CodeInput';
import { RoleSelector, TITLE_OPTIONS } from '@/components/auth/RoleSelector';

// Radix Select rejects an empty-string item value, so "None" needs a sentinel
const NONE = '__none';

const STEPS_CONFIG = [
  { title: 'Your Name', description: 'Basic information' },
  { title: 'Institution', description: 'Academic details' },
  { title: 'Role', description: 'Your background' },
];

// Ark UI keeps every panel mounted and toggles `hidden`, so an unscoped
// animate-in would run once on mount and never replay. Scoping it to the open
// state re-triggers it on each step change.
const STEP_PANEL =
  'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:slide-in-from-right-2 duration-200 ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:animate-none';

// Users with a completed profile don't need onboarding; if they arrived here
// holding an invitation (query param or a stashed token), acceptance happens
// on the invite landing page instead.
function resolveInvitationToken(search: Record<string, unknown>): string | null {
  if (typeof search.invitation === 'string' && search.invitation) {
    return search.invitation;
  }
  if (typeof sessionStorage !== 'undefined') {
    return getPendingInvitationToken();
  }
  return null;
}

// Profiles completed before the real-email step existed still carry the
// synthetic ORCID address; they are sent back through the email step only
function isOnboarded(user: {
  profileCompletedAt?: number | null;
  email?: string | null;
  emailVerified?: boolean | null;
}) {
  return getOnboardingStep(user) === null;
}

function navigateAfterProfile(navigate: ReturnType<typeof useNavigate>) {
  const invitationToken = resolveInvitationToken(
    Object.fromEntries(new URLSearchParams(window.location.search)),
  );
  if (invitationToken) {
    navigate({ to: '/invite/$token', params: { token: invitationToken }, replace: true });
    return;
  }
  navigate({ to: hasPendingPlan() ? ('/settings/plans' as string) : '/dashboard', replace: true });
}

export const Route = createFileRoute('/_auth/complete-profile')({
  ssr: false,
  beforeLoad: ({ location }) => {
    // Auth failures that redirect here with ?error= are explained on the sign-in page
    const search = location.search as Record<string, unknown>;
    if (typeof search.error === 'string' && search.error) {
      throw redirect({ to: '/signin', search: { error: search.error } });
    }

    const state = useAuthStore.getState();
    const user = selectUser(state);

    // Synchronous check: if we already know the profile is complete, redirect
    // immediately without mounting the component. This covers the common case
    // of an existing user signing in (cached or session user is available).
    if (user && isOnboarded(user)) {
      const invitationToken = resolveInvitationToken(location.search as Record<string, unknown>);
      if (invitationToken) {
        throw redirect({ to: '/invite/$token', params: { token: invitationToken } });
      }
      throw redirect({
        to: hasPendingPlan() ? ('/settings/plans' as string) : '/dashboard',
      });
    }
  },
  component: CompleteProfilePage,
});

function CompleteProfilePage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [titleSelection, setTitleSelection] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [institution, setInstitution] = useState('');
  const [department, setDepartment] = useState('');
  const [persona, setPersona] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasEditedName, setHasEditedName] = useState(false);
  const [hasAutofilledName, setHasAutofilledName] = useState(false);

  const navigate = useNavigate();
  const user = useAuthStore(selectUser);
  const isAuthLoading = useAuthStore(selectIsAuthLoading);
  const updateProfile = useAuthStore(s => s.updateProfile);

  // Handles cases beforeLoad couldn't catch synchronously (session was still
  // loading, no cached user yet). Once auth resolves, redirect as needed.
  useEffect(() => {
    if (isAuthLoading) return;

    if (user && isOnboarded(user)) {
      navigateAfterProfile(navigate);
    } else if (!user) {
      navigate({ to: '/signup', replace: true });
    }
  }, [user, isAuthLoading, navigate]);

  const title = useMemo(() => {
    if (titleSelection === 'other') return customTitle.trim();
    if (titleSelection === NONE) return '';
    return titleSelection;
  }, [titleSelection, customTitle]);

  const isCustomTitle = titleSelection === 'other';

  // Pre-fill name from the OAuth session or the email typed at sign-up
  useEffect(() => {
    if (hasEditedName || hasAutofilledName || !user) {
      // For non-authenticated users, try the email typed at sign-up
      if (!user && !hasEditedName && !hasAutofilledName) {
        const pendingName = localStorage.getItem('pendingName');
        if (!firstName.trim() && !lastName.trim() && pendingName) {
          setFirstName(pendingName);
          setHasAutofilledName(true);
        }
      }
      return;
    }

    // If backend created a placeholder name, prefer the submitted email
    const pendingName = localStorage.getItem('pendingName');
    if (
      pendingName &&
      !firstName.trim() &&
      !lastName.trim() &&
      (!user.name || String(user.name).trim().toLowerCase() === 'user')
    ) {
      setFirstName(pendingName);
      setHasAutofilledName(true);
      return;
    }

    // Use structured givenName/familyName from OAuth if available
    if (!firstName.trim() && !lastName.trim()) {
      const u = user as Record<string, unknown>;
      if (u.givenName || u.familyName) {
        setFirstName(String(u.givenName || ''));
        setLastName(String(u.familyName || ''));
        setHasAutofilledName(true);
      } else if (user.name) {
        const nameParts = String(user.name).trim().split(/\s+/).filter(Boolean);
        if (nameParts.length >= 2) {
          setFirstName(nameParts[0]);
          setLastName(nameParts.slice(1).join(' '));
        } else if (nameParts.length === 1) {
          setFirstName(nameParts[0]);
        }
        setHasAutofilledName(true);
      }
    }
  }, [user, hasEditedName, hasAutofilledName, firstName, lastName]);

  // Load pending persona from localStorage
  useEffect(() => {
    const pendingPersona = localStorage.getItem('pendingPersona');
    if (pendingPersona) {
      setPersona(pendingPersona);
      localStorage.removeItem('pendingPersona');
    }
  }, []);

  function validateStep1(): boolean {
    setError('');
    if (!firstName.trim()) {
      setError('Please enter your first name');
      return false;
    }
    return true;
  }

  function handleStep1Next(e: React.FormEvent) {
    e.preventDefault();
    if (validateStep1()) setCurrentStep(1);
  }

  function handleStep2Next(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setCurrentStep(2);
  }

  const handleStepChange = useCallback(
    (details: { step: number }) => {
      if (details.step > currentStep && currentStep === 0) {
        if (!validateStep1()) return;
      }
      setError('');
      setCurrentStep(details.step);
    },
    // validateStep1 reads firstName from closure -- stable enough for this use case
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep],
  );

  async function handleSubmit(selectedPersona = persona) {
    setLoading(true);
    setError('');

    const urlParams = new URLSearchParams(window.location.search);

    try {
      const givenName = firstName.trim();
      const familyName = lastName.trim();
      const fullName = [givenName, familyName].filter(Boolean).join(' ');

      await updateProfile({
        name: fullName,
        givenName: givenName || null,
        familyName: familyName || null,
        persona: selectedPersona || 'other',
        profileCompletedAt: Math.floor(Date.now() / 1000),
        title: title || null,
        institution: institution.trim() || null,
        department: department.trim() || null,
      });

      // Clear signup flags
      localStorage.removeItem('oauthSignup');
      localStorage.removeItem('pendingName');
      localStorage.removeItem('pendingPersona');

      // Handle invitation acceptance
      const invitationToken = urlParams.get('invitation') || getPendingInvitationToken();

      if (invitationToken) {
        try {
          const result = await acceptInvitation({ data: { token: invitationToken } });
          clearPendingInvitationToken();

          if (result.projectId) {
            showToast.success(
              'Invitation Accepted',
              `You've been added to "${result.projectName}"`,
            );
            navigate({ to: `/projects/${result.projectId}` as string, replace: true });
            return;
          }
        } catch (inviteErr) {
          console.error('Failed to accept invitation:', inviteErr);
          // The emailed /invite link is stable, so the user can retry from it
          clearPendingInvitationToken();
          showToast.error(
            'Invitation Not Accepted',
            'Your account was created, but the project invitation could not be accepted. Open the invitation link from your email to try again.',
          );
        }
      }

      // Handle plan redirect from landing pricing page
      const { handled, error: planError } = await handlePendingPlanRedirect({ navigate });

      if (handled) {
        if (planError) {
          showToast.error(
            BILLING_MESSAGES.CHECKOUT_ERROR.title,
            BILLING_MESSAGES.CHECKOUT_ERROR.message,
          );
          navigate({ to: '/settings/plans' as string, replace: true });
        }
        return;
      }

      // Default: go to dashboard
      clearPendingPlan();
      await new Promise(resolve => setTimeout(resolve, 200));
      navigate({ to: '/dashboard', replace: true });
    } catch (err) {
      await handleError(err, { setError, showToast: false });
    } finally {
      setLoading(false);
    }
  }

  // Don't render the form while auth is loading or if the user shouldn't be
  // here (profile already complete / not logged in). The useEffect above
  // handles the redirect; returning null keeps the auth layout background
  // visible with no flash of the form.
  if (isAuthLoading || !user || isOnboarded(user)) {
    return null;
  }

  if (getOnboardingStep(user) === 'email') {
    return <OnboardingEmailStep currentEmail={user.email ?? ''} />;
  }

  return (
    <div className='border-border bg-card relative w-full max-w-md rounded-xl border p-5 pb-2 shadow-2xl sm:max-w-xl sm:rounded-3xl sm:p-10 sm:pb-3'>
      <a href='/' className='absolute top-4 left-4 sm:top-5 sm:left-5'>
        <img src='/logo.svg' alt='CoRATES' className='h-6 w-auto sm:h-7' />
      </a>

      <Steps count={STEPS_CONFIG.length} step={currentStep} onStepChange={handleStepChange} linear>
        {/* Step Indicator */}
        <StepsList className='mb-6 flex items-center justify-center pt-4'>
          {STEPS_CONFIG.map((stepInfo, index) => (
            <StepsItem key={stepInfo.title} index={index} className='flex items-center'>
              <StepsTrigger
                className='group flex flex-col items-center focus:outline-none'
                disabled={index > currentStep + 1}
              >
                <StepsIndicator className='data-complete:bg-primary data-complete:text-primary-foreground data-current:bg-primary data-current:text-primary-foreground data-incomplete:bg-secondary data-incomplete:text-muted-foreground group-hover:data-incomplete:bg-border flex size-8 items-center justify-center rounded-full text-sm font-semibold transition-colors'>
                  {index < currentStep ?
                    <CheckIcon className='size-4' />
                  : index + 1}
                </StepsIndicator>
                <span className='text-muted-foreground mt-1 hidden text-xs sm:block'>
                  {stepInfo.title}
                </span>
              </StepsTrigger>
              {index < STEPS_CONFIG.length - 1 && (
                <StepsSeparator className='data-complete:bg-primary data-current:bg-secondary data-incomplete:bg-secondary mx-2 mt-3.75 h-0.5 w-8 self-start transition-colors sm:w-12' />
              )}
            </StepsItem>
          ))}
        </StepsList>

        <StepsViewport>
          {/* Step 1: Name and Title */}
          <StepsContent index={0} className={STEP_PANEL}>
            <div className='mb-5 text-center'>
              <h2 className='text-foreground mb-1 text-xl font-bold sm:text-2xl'>
                Complete Your Profile
              </h2>
              <p className='text-muted-foreground text-xs sm:text-sm'>
                Just a few details to get you started
              </p>
            </div>

            <form onSubmit={handleStep1Next} className='flex flex-col gap-4' autoComplete='off'>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label className='mb-1' htmlFor='first-name-input'>
                    First Name
                  </Label>
                  <Input
                    type='text'
                    autoComplete='given-name'
                    autoCapitalize='words'
                    spellCheck='false'
                    value={firstName}
                    onChange={e => {
                      setHasEditedName(true);
                      setFirstName(e.target.value);
                    }}
                    className='h-auto py-2 text-sm'
                    required
                    id='first-name-input'
                    placeholder='First'
                    aria-describedby={error ? 'profile-step1-error' : undefined}
                  />
                </div>
                <div>
                  <Label className='mb-1' htmlFor='last-name-input'>
                    Last Name{' '}
                    <span className='text-muted-foreground/70 font-normal'>(optional)</span>
                  </Label>
                  <Input
                    type='text'
                    autoComplete='family-name'
                    autoCapitalize='words'
                    spellCheck='false'
                    value={lastName}
                    onChange={e => {
                      setHasEditedName(true);
                      setLastName(e.target.value);
                    }}
                    className='h-auto py-2 text-sm'
                    id='last-name-input'
                    placeholder='Last'
                    aria-describedby={error ? 'profile-step1-error' : undefined}
                  />
                </div>
              </div>

              <div className='flex flex-col gap-2'>
                <Label htmlFor='title-select' className='mb-1'>
                  Title <span className='text-muted-foreground/70 font-normal'>(optional)</span>
                </Label>
                <Select value={titleSelection} onValueChange={v => setTitleSelection(v)}>
                  <SelectTrigger id='title-select' className='w-full'>
                    <SelectValue placeholder='Select a title (optional)' />
                  </SelectTrigger>
                  <SelectContent>
                    {TITLE_OPTIONS.map(option => (
                      <SelectItem key={option.value || NONE} value={option.value || NONE}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isCustomTitle && (
                  <Input
                    type='text'
                    value={customTitle}
                    onChange={e => setCustomTitle(e.target.value)}
                    className='h-auto py-2 text-sm'
                    placeholder='Enter your title'
                    maxLength={50}
                  />
                )}
              </div>

              <ErrorMessage error={error} id='profile-step1-error' />

              <PrimaryButton loading={false} className='mb-2 sm:mb-3'>
                Next
              </PrimaryButton>
            </form>
          </StepsContent>

          {/* Step 2: Institution Details */}
          <StepsContent index={1} className={STEP_PANEL}>
            <div className='mb-5 text-center'>
              <h2 className='text-foreground mb-1 text-xl font-bold sm:text-2xl'>
                Institution Details
              </h2>
              <p className='text-muted-foreground text-xs sm:text-sm'>
                Optional - helps us understand your background
              </p>
            </div>

            <form onSubmit={handleStep2Next} className='flex flex-col gap-4' autoComplete='off'>
              <div>
                <Label className='mb-1' htmlFor='institution-input'>
                  University / Institution
                </Label>
                <Input
                  type='text'
                  id='institution-input'
                  autoComplete='organization'
                  value={institution}
                  onChange={e => setInstitution(e.target.value)}
                  className='h-auto py-2 text-sm'
                  placeholder='e.g., University of Oxford'
                  maxLength={200}
                />
              </div>

              <div>
                <Label className='mb-1' htmlFor='department-input'>
                  Department / Faculty
                </Label>
                <Input
                  type='text'
                  id='department-input'
                  value={department}
                  onChange={e => setDepartment(e.target.value)}
                  className='h-auto py-2 text-sm'
                  placeholder='e.g., Department of Medicine'
                  maxLength={200}
                />
              </div>

              <ErrorMessage error={error} id='profile-step2-error' />

              <div className='flex gap-3'>
                <StepsPrevTrigger asChild>
                  <Button
                    type='button'
                    variant='outline'
                    className='h-auto flex-1 rounded-lg py-2 font-semibold sm:rounded-xl sm:py-3 sm:text-base'
                  >
                    Back
                  </Button>
                </StepsPrevTrigger>
                <Button
                  type='submit'
                  className='h-auto flex-1 rounded-lg py-2 font-semibold sm:rounded-xl sm:py-3 sm:text-base'
                >
                  Next
                </Button>
              </div>

              <Button
                type='button'
                variant='link'
                onClick={() => setCurrentStep(2)}
                className='text-muted-foreground hover:text-secondary-foreground mx-auto -mt-2'
              >
                Skip for now
              </Button>
            </form>
          </StepsContent>

          {/* Step 3: Persona Selection */}
          <StepsContent index={2} className={STEP_PANEL}>
            <div className='mb-5 text-center'>
              <h2 className='text-foreground mb-1 text-xl font-bold sm:text-2xl'>
                What best describes you?
              </h2>
              <p className='text-muted-foreground text-xs sm:text-sm'>
                You can change this later in settings.
              </p>
            </div>

            <form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit(persona);
              }}
              className='flex flex-col gap-4'
            >
              <RoleSelector selectedRole={persona} onSelect={setPersona} />

              <ErrorMessage error={error} id='profile-step3-error' />

              <div className='flex gap-3'>
                <StepsPrevTrigger asChild>
                  <Button
                    type='button'
                    variant='outline'
                    className='h-auto flex-1 rounded-lg py-2 font-semibold sm:rounded-xl sm:py-3 sm:text-base'
                  >
                    Back
                  </Button>
                </StepsPrevTrigger>
                <PrimaryButton
                  loading={loading}
                  loadingText='Finishing...'
                  disabled={!persona}
                  className='flex-3'
                >
                  Finish Setup
                </PrimaryButton>
              </div>

              <Button
                type='button'
                variant='link'
                onClick={() => handleSubmit('other')}
                disabled={loading}
                className='text-muted-foreground hover:text-secondary-foreground mx-auto -mt-2'
              >
                Skip and finish
              </Button>
            </form>
          </StepsContent>

          <StepsCompletedContent className={STEP_PANEL}>
            <div className='flex flex-col items-center justify-center py-8'>
              <div className='bg-success-bg mb-4 flex size-16 items-center justify-center rounded-full'>
                <CheckIcon className='text-success size-8' />
              </div>
              <h2 className='text-foreground mb-2 text-xl font-bold'>Profile Saved</h2>
              <p className='text-muted-foreground text-sm'>Redirecting to your dashboard...</p>
            </div>
          </StepsCompletedContent>
        </StepsViewport>
      </Steps>
    </div>
  );
}

function OnboardingEmailStep({ currentEmail }: { currentEmail: string }) {
  const requestOnboardingEmailCode = useAuthStore(s => s.requestOnboardingEmailCode);
  const confirmOnboardingEmail = useAuthStore(s => s.confirmOnboardingEmail);
  const hasRealEmail = !!currentEmail && !isSyntheticEmail(currentEmail);
  const [email, setEmail] = useState(hasRealEmail ? currentEmail : '');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await requestOnboardingEmailCode(email.trim());
      setSent(true);
    } catch (err) {
      await handleError(err, { setError, showToast: false });
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm(submitted = code) {
    if (loading) return;
    setError('');
    if (submitted.length !== 6) {
      setError('Enter the six-digit code from your email');
      return;
    }
    setLoading(true);
    try {
      const { claimed } = await confirmOnboardingEmail(email.trim(), submitted);
      if (claimed) {
        showToast.success(
          'Welcome back',
          'That email already had a CoRATES account, so we signed you in to it and connected this sign-in method.',
        );
      }
      // The refreshed session moves this page to the next step or onward
    } catch (err) {
      setCode('');
      await handleError(err, { setError, showToast: false });
      setLoading(false);
    }
  }

  return (
    <div className='border-border bg-card relative w-full max-w-md rounded-xl border p-5 shadow-2xl sm:max-w-xl sm:rounded-3xl sm:p-10'>
      <a href='/' className='absolute top-4 left-4 sm:top-5 sm:left-5'>
        <img src='/logo.svg' alt='CoRATES' className='h-6 w-auto sm:h-7' />
      </a>

      <div className='mb-5 pt-4 text-center'>
        <h2 className='text-foreground mb-1 text-xl font-bold sm:text-2xl'>
          {hasRealEmail ? 'Confirm your email' : 'Add your email'}
        </h2>
        <p className='text-muted-foreground text-xs sm:text-sm'>
          {sent ?
            <>
              We sent a six-digit code to <strong className='text-foreground'>{email}</strong>
            </>
          : hasRealEmail ?
            'Confirm this address so project invitations and notifications can reach you.'
          : 'ORCID did not share an email address with us. Add one so project invitations and notifications can reach you.'
          }
        </p>
      </div>

      {sent ?
        <form
          onSubmit={e => {
            e.preventDefault();
            handleConfirm();
          }}
          className='flex flex-col gap-4'
        >
          <CodeInput
            id='onboarding-email-code'
            value={code}
            onChange={setCode}
            onComplete={handleConfirm}
            disabled={loading}
          />
          <ErrorMessage error={error} id='onboarding-email-error' />
          <PrimaryButton loading={loading} loadingText='Verifying...'>
            Continue
          </PrimaryButton>
          <ResendCode
            onResend={() => requestOnboardingEmailCode(email.trim())}
            onChangeEmail={() => {
              setSent(false);
              setCode('');
              setError('');
            }}
          />
        </form>
      : <form onSubmit={handleSend} className='flex flex-col gap-4' autoComplete='off'>
          <div>
            <Label className='mb-1' htmlFor='contact-email-input'>
              Email
            </Label>
            <Input
              type='email'
              autoComplete='email'
              autoCapitalize='off'
              spellCheck='false'
              value={email}
              onChange={e => setEmail(e.target.value)}
              className='h-auto py-2 text-sm'
              required
              id='contact-email-input'
              placeholder='you@example.com'
              disabled={loading}
              aria-describedby={error ? 'onboarding-email-error' : undefined}
            />
          </div>
          <ErrorMessage error={error} id='onboarding-email-error' />
          <PrimaryButton loading={loading} loadingText='Sending Code...'>
            Send Code
          </PrimaryButton>
        </form>
      }
    </div>
  );
}
